import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import ScrollToTop from "./components/ScrollToTop";
import Footer from "./components/Footer";
import HomePage from "./pages/HomePage";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import Ad3Page from "./pages/Ad3Page";
import LoginPage from "./components/auth/LoginPage";
import SignupPage from "./components/auth/SignupPage";
import ForgotPasswordPage from "./components/auth/ForgotPasswordPage";
import DashboardLayout from "./components/dashboard/DashboardLayout";
import DashboardHome from "./components/dashboard/DashboardHome";
import LeadsPage from "./components/dashboard/LeadsPage";
import CallsPage from "./components/dashboard/CallsPage";
import AppointmentsPage from "./components/dashboard/AppointmentsPage";
import MessagingPage from "./components/dashboard/MessagingPage";
import SettingsPage from "./components/dashboard/SettingsPage";
import AnalyticsPage from "./components/dashboard/AnalyticsPage";
import OnboardingWizard from "./components/onboarding/OnboardingWizard";
import AdminPage from "./pages/AdminPage";
import ComingSoonPage from "./components/dashboard/ComingSoonPage";
import KnowledgeBasePage from "./components/dashboard/KnowledgeBasePage";
import AvaBrainPage from "./components/dashboard/AvaBrainPage";
import CreditsPage from "./components/dashboard/CreditsPage";
import { Toaster } from "sonner";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f9f8f4] dark:bg-[#0a0a0a]">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

// super_admin users have nothing to do in the customer dashboard — bounce
// them to /admin no matter how they got here (refresh, bookmark, sidebar, etc.).
function CustomerDashboardOnly({ children }) {
  const { user } = useAuth();
  if (user?.role === 'super_admin') {
    return <Navigate to="/admin" replace />;
  }
  return children;
}

function MarketingLayout({ children }) {
  return (
    <div className="min-h-screen flex flex-col">
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}

function AuthLayout({ children }) {
  return (
    <div className="min-h-screen flex flex-col bg-[#f9f8f4] dark:bg-[#0a0a0a]">
      <main className="flex-1">{children}</main>
      <div className="text-center py-4 border-t border-gray-200/50 dark:border-white/5">
        <a href="https://www.averoan.com" target="_blank" rel="noopener noreferrer" className="text-xs text-gray-400 hover:text-gray-600 dark:text-slate-500 dark:hover:text-slate-300 transition-colors">
          Powered by Averoan Technologies Inc.
        </a>
      </div>
    </div>
  );
}

function AppRoutes() {
  const hostname = window.location.hostname;
  const params = new URLSearchParams(window.location.search);
  const isAdminSubdomain = hostname === 'next.nextbell.ai' || hostname.startsWith('next.') || params.get('mode') === 'admin';
  
  if (isAdminSubdomain) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      {/* Marketing */}
      <Route path="/" element={<MarketingLayout><HomePage /></MarketingLayout>} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/ad3" element={<MarketingLayout><Ad3Page /></MarketingLayout>} />

      {/* Auth */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<AuthLayout><SignupPage /></AuthLayout>} />
      <Route path="/forgot-password" element={<AuthLayout><ForgotPasswordPage /></AuthLayout>} />

      {/* Onboarding (post-signup wizard) */}
      <Route path="/onboarding" element={<ProtectedRoute><OnboardingWizard /></ProtectedRoute>} />

      {/* Dashboard */}
      <Route path="/dashboard" element={<ProtectedRoute><CustomerDashboardOnly><DashboardLayout /></CustomerDashboardOnly></ProtectedRoute>}>
        <Route index element={<DashboardHome />} />
        <Route path="leads" element={<LeadsPage />} />
        <Route path="calls" element={<CallsPage />} />
        <Route path="appointments" element={<AppointmentsPage />} />
        <Route path="messaging" element={<MessagingPage />} />
        <Route path="analytics" element={<AnalyticsPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="credits" element={<CreditsPage />} />
        <Route path="ava" element={<AvaBrainPage />} />
        <Route path="knowledge" element={<KnowledgeBasePage />} />
        {/* Industry-specific placeholders (full pages are still on the roadmap) */}
        <Route path="waitlist" element={<ComingSoonPage feature="Waitlist" industry="restaurants" />} />
        <Route path="patients" element={<ComingSoonPage feature="Patients" industry="medical & dental practices" />} />
        <Route path="estimates" element={<ComingSoonPage feature="Estimates" industry="contractors" />} />
        <Route path="matters" element={<ComingSoonPage feature="Matters" industry="law firms" />} />
        <Route path="listings" element={<ComingSoonPage feature="Listings" industry="real estate teams" />} />
      </Route>

      {/* Admin */}
      <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />

      {/* Catch-all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <ScrollToTop />
          <AppRoutes />
          <Toaster position="top-right" theme="dark" richColors closeButton />
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
